import React, { useState, useMemo } from 'react';
import {
  Search,
  Filter,
  Heart,
  RotateCw,
  Plus,
  Shirt,
  Sparkles,
  Tag,
  Check,
  X,
  Layers,
  ArrowUpDown,
  Flame,
  Clock,
  Award,
} from 'lucide-react';
import { ClothingItem, CategoryType } from '../types';

interface ClosetViewProps {
  clothes: ClothingItem[];
  brands: string[];
  sizes: string[];
  isLoading: boolean;
  onOpenAddItem: () => void;
  onSelectItem: (item: ClothingItem) => void;
  onToggleFavorite: (id: string, e: React.MouseEvent) => void;
  onLogWear: (id: string, e: React.MouseEvent) => void;
  onSendToTryOn: (item: ClothingItem, e: React.MouseEvent) => void;
}

const CATEGORIES: { key: string; label: string; icon: string }[] = [
  { key: 'all', label: 'All Items', icon: '✨' },
  { key: 'tops', label: 'Tops', icon: '👕' },
  { key: 'bottoms', label: 'Bottoms', icon: '👖' },
  { key: 'outerwear', label: 'Jackets & Outerwear', icon: '🧥' },
  { key: 'shoes', label: 'Shoes', icon: '👟' },
  { key: 'accessories', label: 'Accessories', icon: '🧢' },
];

export const ClosetView: React.FC<ClosetViewProps> = ({
  clothes,
  brands,
  sizes,
  isLoading,
  onOpenAddItem,
  onSelectItem,
  onToggleFavorite,
  onLogWear,
  onSendToTryOn,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [onlyFavorites, setOnlyFavorites] = useState<boolean>(false);
  const [wearFilter, setWearFilter] = useState<'all' | 'unworn' | 'frequent'>('all');
  const [sortBy, setSortBy] = useState<'most_worn' | 'least_worn' | 'favorites' | 'newest' | 'name' | 'brand'>('newest');
  const [flippedCards, setFlippedCards] = useState<Record<string, boolean>>({});

  // Toggle front/back flip for a card
  const toggleFlip = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFlippedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Filtered and Sorted Clothes
  const filteredClothes = useMemo(() => {
    let result = clothes.filter((item) => {
      // Category filter
      if (selectedCategory !== 'all' && item.category !== selectedCategory) {
        return false;
      }
      // Favorites filter
      if (onlyFavorites && !item.is_favorite) {
        return false;
      }
      // Wear rotation filter
      if (wearFilter === 'unworn' && (item.wear_count || 0) > 0) {
        return false;
      }
      if (wearFilter === 'frequent' && (item.wear_count || 0) < 3) {
        return false;
      }
      // Brand filter
      if (selectedBrand && item.brand?.toLowerCase() !== selectedBrand.toLowerCase()) {
        return false;
      }
      // Size filter
      if (selectedSize && item.size?.toLowerCase() !== selectedSize.toLowerCase()) {
        return false;
      }
      // Season filter
      if (selectedSeason && item.season && item.season !== 'All-Season' && item.season !== selectedSeason) {
        return false;
      }
      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = item.name.toLowerCase().includes(q);
        const matchBrand = item.brand?.toLowerCase().includes(q);
        const matchCategory = item.category.toLowerCase().includes(q);
        const matchSub = item.subcategory?.toLowerCase().includes(q);
        const matchColor = item.color?.toLowerCase().includes(q);
        const matchTags = item.tags?.toLowerCase().includes(q);
        if (!matchName && !matchBrand && !matchCategory && !matchSub && !matchColor && !matchTags) {
          return false;
        }
      }
      return true;
    });

    // Apply Sorting
    result = [...result].sort((a, b) => {
      if (sortBy === 'most_worn') {
        // Sort highest wear_count first
        const diff = (b.wear_count || 0) - (a.wear_count || 0);
        if (diff !== 0) return diff;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
      if (sortBy === 'least_worn') {
        // Sort lowest wear_count first
        const diff = (a.wear_count || 0) - (b.wear_count || 0);
        if (diff !== 0) return diff;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
      if (sortBy === 'favorites') {
        if (b.is_favorite !== a.is_favorite) return b.is_favorite - a.is_favorite;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === 'brand') {
        return (a.brand || '').localeCompare(b.brand || '');
      }
      // Default: newest first
      return new Date(b.created_at || b.updated_at).getTime() - new Date(a.created_at || a.updated_at).getTime();
    });

    return result;
  }, [clothes, selectedCategory, onlyFavorites, wearFilter, selectedBrand, selectedSize, selectedSeason, searchQuery, sortBy]);

  // Counts by category
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: clothes.length };
    clothes.forEach((item) => {
      c[item.category] = (c[item.category] || 0) + 1;
    });
    return c;
  }, [clothes]);

  const hasActiveFilters = !!(
    selectedBrand ||
    selectedSize ||
    selectedSeason ||
    onlyFavorites ||
    wearFilter !== 'all' ||
    searchQuery
  );

  const clearFilters = () => {
    setSelectedBrand('');
    setSelectedSize('');
    setSelectedSeason('');
    setOnlyFavorites(false);
    setWearFilter('all');
    setSearchQuery('');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Category Pills */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-2 scrollbar-none">
        {CATEGORIES.map((cat) => {
          const count = counts[cat.key] || 0;
          const isActive = selectedCategory === cat.key;
          return (
            <button
              key={cat.key}
              onClick={() => setSelectedCategory(cat.key)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-brand-600 text-white shadow-lg shadow-brand-600/25 scale-[1.02]'
                  : 'bg-slate-900/90 text-slate-300 hover:bg-slate-850 hover:text-white border border-slate-800'
              }`}
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full ${
                  isActive ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-slate-900/80 border border-slate-800/90 rounded-3xl p-4 sm:p-5 shadow-sm backdrop-blur-md space-y-3.5">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by garment name, brand, color, style..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Sort Dropdown */}
          <div className="flex items-center space-x-1.5 bg-slate-950 px-3 py-2 rounded-xl border border-slate-800 shrink-0">
            <ArrowUpDown className="w-3.5 h-3.5 text-brand-400 shrink-0" />
            <span className="text-xs text-slate-400 font-medium hidden sm:inline">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="bg-transparent text-slate-200 text-xs sm:text-sm font-semibold focus:outline-none cursor-pointer"
            >
              <option value="most_worn">🔥 Most Worn First</option>
              <option value="least_worn">❄️ Least Worn / Unworn First</option>
              <option value="newest">🕒 Recently Added</option>
              <option value="favorites">⭐ Favorites First</option>
              <option value="name">🔤 Item Name (A-Z)</option>
              <option value="brand">🏷️ Brand (A-Z)</option>
            </select>
          </div>
        </div>

        {/* Quick Filter Row */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {/* Wear Rotation Quick Pills */}
          <div className="flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800/80 text-xs">
            <button
              onClick={() => setWearFilter('all')}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all ${
                wearFilter === 'all'
                  ? 'bg-slate-800 text-white shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              All Wear
            </button>
            <button
              onClick={() => setWearFilter('unworn')}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all flex items-center gap-1 ${
                wearFilter === 'unworn'
                  ? 'bg-sky-500/20 text-sky-300 border border-sky-500/30'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="Items with 0 wear count"
            >
              <span>❄️ Never Worn</span>
            </button>
            <button
              onClick={() => setWearFilter('frequent')}
              className={`px-2.5 py-1 rounded-lg font-semibold transition-all flex items-center gap-1 ${
                wearFilter === 'frequent'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="Items worn 3 or more times"
            >
              <span>🔥 Heavy Rotation (3+)</span>
            </button>
          </div>

          {/* Brand Filter */}
          {brands.length > 0 && (
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="bg-slate-950 border border-slate-800 text-slate-200 text-xs sm:text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500/50"
            >
              <option value="">All Brands</option>
              {brands.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          )}

          {/* Size Filter */}
          {sizes.length > 0 && (
            <select
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
              className="bg-slate-950 border border-slate-800 text-slate-200 text-xs sm:text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500/50"
            >
              <option value="">All Sizes</option>
              {sizes.map((s) => (
                <option key={s} value={s}>
                  Size: {s}
                </option>
              ))}
            </select>
          )}

          {/* Season Filter */}
          <select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            className="bg-slate-950 border border-slate-800 text-slate-200 text-xs sm:text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500/50"
          >
            <option value="">All Seasons</option>
            <option value="Spring">Spring</option>
            <option value="Summer">Summer</option>
            <option value="Fall">Fall / Autumn</option>
            <option value="Winter">Winter</option>
          </select>

          {/* Favorite Toggle */}
          <button
            onClick={() => setOnlyFavorites(!onlyFavorites)}
            className={`px-3 py-2 rounded-xl border transition-all flex items-center gap-1.5 text-xs font-semibold ${
              onlyFavorites
                ? 'bg-rose-500/20 border-rose-500/40 text-rose-300'
                : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${onlyFavorites ? 'fill-rose-500 text-rose-500' : ''}`} />
            <span>Favorites</span>
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-brand-400 hover:text-brand-300 px-2 py-1 font-semibold transition-colors"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* Garments Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="bg-slate-900 border border-slate-800/80 rounded-3xl h-72 animate-pulse"
            />
          ))}
        </div>
      ) : filteredClothes.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 border border-dashed border-slate-800 rounded-3xl p-8 max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-2xl bg-brand-500/10 text-brand-400 flex items-center justify-center mx-auto mb-4 border border-brand-500/20">
            <Shirt className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">No clothing items found</h3>
          <p className="text-sm text-slate-400 mb-6">
            {clothes.length === 0
              ? 'Start building your virtual closet by adding your first piece of clothing!'
              : 'No items match your active search or filters. Try clearing your filters.'}
          </p>
          {clothes.length === 0 ? (
            <button
              onClick={onOpenAddItem}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow-lg shadow-brand-600/30 transition-all hover:scale-105"
            >
              <Plus className="w-4 h-4" />
              <span>Add First Garment</span>
            </button>
          ) : (
            <button
              onClick={clearFilters}
              className="text-sm text-brand-400 font-semibold hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {filteredClothes.map((item) => {
            const isFlipped = !!flippedCards[item.id];
            const frontImg = item.image_cropped || item.image_front;
            const backImg = item.image_cropped_back || item.image_back;
            const hasBack = !!backImg;
            const wearCount = item.wear_count || 0;

            return (
              <div
                key={item.id}
                onClick={() => onSelectItem(item)}
                className="group relative bg-slate-900/90 hover:bg-slate-850 border border-slate-800/90 hover:border-brand-500/40 rounded-3xl overflow-hidden shadow-lg hover:shadow-brand-500/10 transition-all duration-300 flex flex-col cursor-pointer"
              >
                {/* Image Container with 3D Flip */}
                <div className="relative aspect-[4/5] w-full bg-slate-950 overflow-hidden perspective-1000">
                  <div
                    className={`w-full h-full relative transition-transform duration-500 transform-style-3d ${
                      isFlipped ? 'rotate-y-180' : ''
                    }`}
                  >
                    {/* Front Face */}
                    <div className="absolute inset-0 w-full h-full backface-hidden">
                      <img
                        src={frontImg}
                        alt={item.name}
                        className="w-full h-full object-contain p-2.5 transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>

                    {/* Back Face */}
                    {hasBack && (
                      <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-slate-950">
                        <img
                          src={backImg}
                          alt={`${item.name} Back`}
                          className="w-full h-full object-contain p-2.5 transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </div>

                  {/* Brand Badge (Top-Left) */}
                  {item.brand && (
                    <div className="absolute top-2.5 left-2.5 z-10">
                      <span className="px-2.5 py-1 rounded-lg bg-slate-900/85 backdrop-blur-md text-white font-extrabold text-[10px] sm:text-[11px] tracking-wide uppercase border border-white/10 shadow-md">
                        {item.brand}
                      </span>
                    </div>
                  )}

                  {/* Size Badge (Top-Right) */}
                  {item.size && (
                    <div className="absolute top-2.5 right-2.5 z-10">
                      <span className="px-2 py-0.5 rounded-lg bg-brand-500/20 backdrop-blur-md text-brand-300 font-bold text-[11px] sm:text-xs border border-brand-500/30 shadow-md">
                        {item.size}
                      </span>
                    </div>
                  )}

                  {/* Flip Card Action Button */}
                  {hasBack && (
                    <button
                      onClick={(e) => toggleFlip(item.id, e)}
                      title="Flip to see front/back"
                      className="absolute bottom-2.5 left-2.5 z-10 px-2 py-1 rounded-lg bg-slate-900/80 hover:bg-slate-800 text-slate-300 hover:text-white text-[10px] font-semibold border border-slate-700/60 backdrop-blur-md flex items-center gap-1 shadow-md transition-colors"
                    >
                      <RotateCw className="w-3 h-3" />
                      <span>{isFlipped ? 'Back' : 'Front'}</span>
                    </button>
                  )}

                  {/* Favorite Button */}
                  <button
                    onClick={(e) => onToggleFavorite(item.id, e)}
                    title="Toggle Favorite"
                    className="absolute bottom-2.5 right-2.5 z-10 p-1.5 rounded-lg bg-slate-900/80 hover:bg-slate-800 text-slate-300 hover:text-rose-400 border border-slate-700/60 backdrop-blur-md shadow-md transition-colors"
                  >
                    <Heart
                      className={`w-3.5 h-3.5 ${
                        item.is_favorite ? 'fill-rose-500 text-rose-500' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* Card Meta & Details */}
                <div className="p-3 sm:p-4 flex-1 flex flex-col justify-between">
                  <div>
                    <h4 className="font-bold text-xs sm:text-sm text-slate-100 truncate group-hover:text-brand-300 transition-colors">
                      {item.name}
                    </h4>

                    <div className="flex items-center space-x-1.5 mt-1 text-[11px] text-slate-400">
                      <span>{item.subcategory || item.category}</span>
                      {item.color && (
                        <>
                          <span>•</span>
                          <span className="truncate">{item.color}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Wear Count & 1-Tap Worn Logger */}
                  <div className="mt-3 pt-2.5 border-t border-slate-800/80 flex items-center justify-between gap-1.5">
                    {/* Wear Badge */}
                    <div
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-bold ${
                        wearCount >= 4
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                          : wearCount > 0
                          ? 'bg-slate-800 text-slate-300'
                          : 'bg-slate-950 text-slate-500'
                      }`}
                      title={`${wearCount} times worn`}
                    >
                      {wearCount >= 4 ? (
                        <Flame className="w-3 h-3 text-amber-400 fill-amber-400" />
                      ) : (
                        <Shirt className="w-3 h-3" />
                      )}
                      <span>{wearCount > 0 ? `${wearCount}x` : 'Unworn'}</span>
                    </div>

                    {/* 1-Tap "Wore Today" Button */}
                    <button
                      type="button"
                      onClick={(e) => onLogWear(item.id, e)}
                      title="Tap to record that you wore this item today"
                      className="inline-flex items-center space-x-1 px-2 py-1 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 text-[10px] sm:text-xs font-bold transition-all hover:scale-105 active:scale-95 shadow-sm"
                    >
                      <span>+1 Wore</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
